<template>
  <AdminAssetTable
    :config="projectsConfig"
    :items="projectsData"
    :users="users"
    :api-service="projectsApiService"
    @refresh="loadProjects"
    @created="handleProjectCreated"
    @updated="handleProjectUpdated"
    @deleted="handleProjectDeleted"
  />
</template>

<script setup lang="ts">
import { projects } from '@/api/projects';
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { Project } from '@/api/types/entities/project/types';
import { fetchAllUsers } from '@/api/users';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { onMounted, ref } from 'vue';

// Configuration
const projectsConfig = ADMIN_ASSET_CONFIGS.projects;

// État
const projectsData = ref<Project[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

// Service API adapté au format attendu par AdminAssetTable
const projectsApiService = {
  async getAll(): Promise<Project[]> {
    return await projects.getAllAdminWithDeleted();
  },
  
  async create(data: Record<string, unknown>): Promise<Project> {
    return await projects.createProject(data as any);
  },
  
  async update(uuid: string, data: Record<string, unknown>): Promise<Project> {
    return await projects.updateProject(uuid, data as any);
  },
  
  async delete(uuid: string): Promise<void> {
    return await projects.deleteProject(uuid);
  },
  async hardDelete(uuid: string): Promise<void> {
    return await projects.hardDeleteProject(uuid);
  },
  async restore(uuid: string): Promise<void> {
    return await projects.restoreProject(uuid);
  }
};

// Méthodes
const loadProjects = async (): Promise<void> => {
  loading.value = true;
  try {
    projectsData.value = await projectsApiService.getAll();
  } catch (error) {
    console.error('Erreur lors du chargement des projets:', error);
  } finally {
    loading.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    users.value = await fetchAllUsers();
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  }
};

// Gestionnaires d'événements
const handleProjectCreated = (project: Entity): void => {
  projectsData.value.push(project as Project);
};

const handleProjectUpdated = (updatedProject: Entity): void => {
  const index = projectsData.value.findIndex((a: Project): boolean => a.uuid === updatedProject.uuid);
  if (index !== -1) {
    projectsData.value[index] = updatedProject as Project;
  }
};

const handleProjectDeleted = (uuid: string): void => {
  projectsData.value = projectsData.value.filter((a: Project): boolean => a.uuid !== uuid);
};

// Lifecycle
onMounted(async (): Promise<void> => {
  await Promise.all([
    loadProjects(),
    loadUsers()
  ]);
});
</script>

<style scoped>
.button-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.button-group button {
  width: 100%;
}
</style> 